import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { GoldGradient } from './components/ui/GoldGradient';
import { SectionDivider } from './components/ui/SectionDivider';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { useLanguage } from './i18n/LanguageContext';
// Below-the-fold sections are lazy-loaded — they download in parallel
// while the curtain is showing, but don't block the initial paint.
const Gallery = lazy(() => import('./components/Gallery').then(m => ({
  default: m.Gallery
})));
const Opening = lazy(() => import('./components/Opening').then(m => ({
  default: m.Opening
})));
const Problem = lazy(() => import('./components/Problem').then(m => ({
  default: m.Problem
})));
const Solution = lazy(() => import('./components/Solution').then(m => ({
  default: m.Solution
})));
const Science = lazy(() => import('./components/Science').then(m => ({
  default: m.Science
})));
const Story = lazy(() => import('./components/Story').then(m => ({
  default: m.Story
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const WhatIncluded = lazy(() => import('./components/WhatIncluded').then(m => ({
  default: m.WhatIncluded
})));
const Services = lazy(() => import('./components/Services').then(m => ({
  default: m.Services
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Contact = lazy(() => import('./components/Contact').then(m => ({
  default: m.Contact
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const WhatsAppFloat = lazy(() => import('./components/ui/WhatsAppFloat').then(m => ({
  default: m.WhatsAppFloat
})));
const blank = <div className="bg-riam-black" data-id="element-392" />;
function useSeoHead() {
  const {
    lang
  } = useLanguage();
  useEffect(() => {
    const meta = {
      es: {
        title: 'Riam Beauty Studio | Luxury Hair Salon & Best Balayage in Queens, NY',
        description: 'Riam Beauty Studio es un Luxury Hair Salon en Sunnyside, Queens, reconocido por el Best Balayage in Queens. Color, corrección, rubios y maquillaje con +15 años de experiencia. Reserva tu cita.'
      },
      en: {
        title: 'Riam Beauty Studio | Luxury Hair Salon & Best Balayage in Queens, NY',
        description: 'Riam Beauty Studio is a Luxury Hair Salon in Sunnyside, Queens, known for the Best Balayage in Queens. Expert color, color correction, blondes & makeup with 15+ years of experience. Book your appointment.'
      }
    };
    const {
      title,
      description
    } = meta[lang] ?? meta.es;
    document.title = title;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', description);
    document.documentElement.lang = lang;
    // Open Graph tags for richer sharing / SEO
    const setOg = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setOg('og:title', title);
    setOg('og:description', description);
    setOg('og:type', 'website');
  }, [lang]);
}
export function SiteContent() {
  useSeoHead();
  return <>
      <GoldGradient data-id="element-393" />
      <Header data-id="element-394" />
      <main data-id="element-395">
        <Hero data-id="element-396" />
        <SectionDivider data-id="element-397" />
        <Suspense fallback={blank} data-id="element-398">
          <Gallery data-id="element-399" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-400">
          <Opening data-id="element-401" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-402">
          <Problem data-id="element-403" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-404">
          <Solution data-id="element-405" />
        </Suspense>
        <SectionDivider data-id="element-406" />
        <Suspense fallback={blank} data-id="element-407">
          <Science data-id="element-408" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-409">
          <Story data-id="element-410" />
        </Suspense>
        <SectionDivider data-id="element-411" />
        <Suspense fallback={blank} data-id="element-412">
          <Testimonials data-id="element-413" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-414">
          <WhatIncluded data-id="element-415" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-416">
          <Services data-id="element-417" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-418">
          <Guarantee data-id="element-419" />
        </Suspense>
        <SectionDivider data-id="element-420" />
        <Suspense fallback={blank} data-id="element-421">
          <FAQ data-id="element-422" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-423">
          <FinalCTA data-id="element-424" />
        </Suspense>
        <Suspense fallback={blank} data-id="element-425">
          <Contact data-id="element-426" />
        </Suspense>
      </main>
      <Suspense fallback={blank} data-id="element-427">
        <Footer data-id="element-428" />
      </Suspense>
      <Suspense fallback={null} data-id="element-429">
        <WhatsAppFloat data-id="element-430" />
      </Suspense>
    </>;
}